import { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../services/api';
import { fmtDate } from './format';
import { c, t, tbl } from '../../theme';
import { useSort, sortRows, SortTh } from '../Shared/sortable';

// Helper/Trainee Roster — the source workbook's "HELPER DYS" section, for
// the Driver-category workers DriverQualificationTable excludes (Commercial
// Helper, DYS Helper Trainee, Residential Helper, DYS Support Truck Driver
// Trainee — see staffingConfig.isHelperOrTrainee). Much simpler than Driver
// Roster: no truck-type qualification grid (migration 116 /
// routes/staffing.js GET+PATCH /helpers).
export default function HelperRosterTable({ helpers, canEdit = false }) {
  const showPeople = helpers.some((h) => 'worker_name' in h);
  const sort = useSort();
  const sorted = sortRows(helpers, sort.key, sort.dir, {
    worker_name: (h) => h.worker_name || '',
    job_profile: (h) => h.job_profile || '',
    manager:     (h) => h.manager || '',
  });
  return (
    <div style={s.tableScroll}>
      <table style={s.table}>
        <thead>
          <tr>
            {showPeople && <SortTh label="Employee" sortKey="worker_name" sort={sort} sticky />}
            <SortTh label="Job Profile" sortKey="job_profile" sort={sort} sticky />
            <SortTh label="Supervisor" sortKey="manager" sort={sort} sticky />
            <SortTh label="Helper" sticky />
            <SortTh label="Scout Trainee" sticky />
            <SortTh label="Promotion Date" sticky />
            <SortTh label="Shield Rides" sticky />
          </tr>
        </thead>
        <tbody>
          {sorted.map((h) => (
            <HelperRow key={h.worker_employee_id || h.id} helper={h} canEdit={canEdit} showPeople={showPeople} />
          ))}
          {!helpers.length && (
            <tr><td colSpan={showPeople ? 7 : 6} style={s.empty}>No helpers or trainees here.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function HelperRow({ helper: h, canEdit, showPeople }) {
  const qc = useQueryClient();
  const [helperDate, setHelperDate] = useState(h.helper_date ? String(h.helper_date).slice(0, 10) : '');
  const [scoutTraineeDate, setScoutTraineeDate] = useState(h.scout_trainee_date ? String(h.scout_trainee_date).slice(0, 10) : '');
  const [promotionDate, setPromotionDate] = useState(h.promotion_date ? String(h.promotion_date).slice(0, 10) : '');
  const [shieldRides, setShieldRides] = useState(h.shield_rides ?? '');

  // Re-sync on prop change, not just on mount — see VacancyTable.jsx's
  // VacancyRow for why.
  useEffect(() => {
    setHelperDate(h.helper_date ? String(h.helper_date).slice(0, 10) : '');
    setScoutTraineeDate(h.scout_trainee_date ? String(h.scout_trainee_date).slice(0, 10) : '');
    setPromotionDate(h.promotion_date ? String(h.promotion_date).slice(0, 10) : '');
    setShieldRides(h.shield_rides ?? '');
  }, [h.helper_date, h.scout_trainee_date, h.promotion_date, h.shield_rides]);

  const saveMutation = useMutation({
    mutationFn: (payload) => api.patch(`/staffing/helpers/${h.worker_employee_id}`, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['staffing-helpers'] });
      qc.invalidateQueries({ queryKey: ['staffing-costcenter'] });
    },
  });

  // Every save upserts the whole row (see PATCH /helpers) — resend every
  // field, same basePayload() pattern as everywhere else in this module.
  function currentPayload(overrides = {}) {
    return {
      helper_date: helperDate || null, scout_trainee_date: scoutTraineeDate || null,
      promotion_date: promotionDate || null, shield_rides: shieldRides || null, ...overrides,
    };
  }
  function saveHelperDate(v) { setHelperDate(v); saveMutation.mutate(currentPayload({ helper_date: v || null })); }
  function saveScoutTraineeDate(v) { setScoutTraineeDate(v); saveMutation.mutate(currentPayload({ scout_trainee_date: v || null })); }
  function savePromotionDate(v) { setPromotionDate(v); saveMutation.mutate(currentPayload({ promotion_date: v || null })); }
  function saveShieldRides() { saveMutation.mutate(currentPayload()); }

  const canEditThisRow = canEdit && !!h.worker_employee_id;

  return (
    <tr style={tbl.tr}>
      {showPeople && (
        <td style={tbl.td}>{h.worker_name}{h.worker_employee_id ? ` (${h.worker_employee_id})` : ''}</td>
      )}
      <td style={s.tdDesc} title={h.job_profile}>{h.job_profile || '—'}</td>
      <td style={tbl.td}>{h.manager || '—'}</td>
      <td style={tbl.td}>
        {canEditThisRow ? (
          <input type="date" style={s.editInput} value={helperDate} onChange={(e) => saveHelperDate(e.target.value)} />
        ) : (fmtDate(h.helper_date))}
      </td>
      <td style={tbl.td}>
        {canEditThisRow ? (
          <input type="date" style={s.editInput} value={scoutTraineeDate} onChange={(e) => saveScoutTraineeDate(e.target.value)} />
        ) : (fmtDate(h.scout_trainee_date))}
      </td>
      <td style={tbl.td}>
        {canEditThisRow ? (
          <input type="date" style={s.editInput} value={promotionDate} onChange={(e) => savePromotionDate(e.target.value)} />
        ) : (fmtDate(h.promotion_date))}
      </td>
      <td style={s.tdNotes}>
        {canEditThisRow ? (
          <input type="text" style={s.editInputWide} value={shieldRides}
            placeholder="e.g. 6/4/2026" onChange={(e) => setShieldRides(e.target.value)} onBlur={saveShieldRides} />
        ) : (h.shield_rides || '—')}
      </td>
    </tr>
  );
}

const s = {
  tableScroll: { overflow: 'auto', maxHeight: 480 },
  table:       { width: '100%', borderCollapse: 'collapse', fontSize: t.bodySm.fontSize },
  tdDesc:      { ...tbl.td, maxWidth: '220px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  tdNotes:     { ...tbl.td, minWidth: '140px' },
  empty:       { textAlign: 'center', padding: '24px', color: c.textFaint },
  editInput:   { width: '130px', padding: '4px 6px', border: `1px solid ${c.borderStrong}`, borderRadius: '4px', fontSize: t.caption.fontSize, fontFamily: 'inherit' },
  editInputWide: { width: '100%', minWidth: '140px', padding: '4px 6px', border: `1px solid ${c.borderStrong}`, borderRadius: '4px', fontSize: t.caption.fontSize, fontFamily: 'inherit' },
};
